import { useMemo, useState } from 'react'
import { CheckCircle2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { useToggleStepProgress } from '@/features/doer/use-doer'
import {
  useAcquireMaterial,
  useAcquireTool,
  useMaterialAcquisitions,
  useToolAcquisitions,
  useUnacquireMaterial,
  useUnacquireTool,
} from '@/features/acquisitions/use-acquisitions'
import { computeStepReadiness } from '@/features/projects/step-readiness'
import { DoingStepRow } from './doing-step-row'
import type { FollowingDetail, MaterialAcquisition, ToolAcquisition } from '@/api/types'

interface DoingOutlineProps {
  following: FollowingDetail
}

/**
 * Read-only renderer of the Doer's snapshot tree with per-Action progress checkboxes. Mobile-
 * first card around a recursive {@link DoingStepRow} list. The "you're done!" empty state shows
 * when all leaf Actions are complete (zero is also "done" — there's nothing to do).
 */
export const DoingOutline = ({ following }: DoingOutlineProps) => {
  const completedStepIds = useMemo(
    () => new Set(following.progress.map((p) => p.snapshotStepId)),
    [following.progress]
  )
  // Phase 13: readiness map — blocked-by titles per Step in the snapshot. Pure function over
  // (snapshotSteps, completedStepIds); recomputes whenever progress flips. Threaded into each
  // DoingStepRow so the Action treatment can mute / show "Blocked by …".
  const readinessMap = useMemo(
    () => computeStepReadiness(following.snapshotSteps, completedStepIds),
    [following.snapshotSteps, completedStepIds]
  )
  const totalActions = useMemo(
    () => countActionLeaves(following.snapshotSteps),
    [following.snapshotSteps]
  )
  const completedInTree = useMemo(
    () => countCompletedInTree(following.snapshotSteps, completedStepIds),
    [following.snapshotSteps, completedStepIds]
  )

  const [pendingStepIds, setPendingStepIds] = useState<Set<string>>(new Set())
  const toggleMutation = useToggleStepProgress()

  // Phase 12: parallel queries (not inlined in FollowingDetail — see PHASE12_STATUS.md drift)
  const materialAcqs = useMaterialAcquisitions(following.projectId)
  const toolAcqs = useToolAcquisitions(following.projectId)
  const acquireMat = useAcquireMaterial(following.projectId)
  const unacquireMat = useUnacquireMaterial(following.projectId)
  const acquireTool = useAcquireTool(following.projectId)
  const unacquireTool = useUnacquireTool(following.projectId)
  const [pendingMaterialIds, setPendingMaterialIds] = useState<Set<string>>(new Set())
  const [pendingToolIds, setPendingToolIds] = useState<Set<string>>(new Set())

  const materialAcqMap = useMemo<Map<string, MaterialAcquisition>>(() => {
    const m = new Map<string, MaterialAcquisition>()
    for (const a of materialAcqs.data ?? []) m.set(a.snapshotAttachmentId, a)
    return m
  }, [materialAcqs.data])
  const toolAcqMap = useMemo<Map<string, ToolAcquisition>>(() => {
    const m = new Map<string, ToolAcquisition>()
    for (const a of toolAcqs.data ?? []) m.set(a.catalogToolId, a)
    return m
  }, [toolAcqs.data])

  const handleToggleMaterial = (snapshotAttachmentId: string, currentlyAcquired: boolean) => {
    setPendingMaterialIds((s) => new Set(s).add(snapshotAttachmentId))
    const finished = () =>
      setPendingMaterialIds((s) => {
        const next = new Set(s)
        next.delete(snapshotAttachmentId)
        return next
      })
    if (currentlyAcquired) {
      unacquireMat.mutate(snapshotAttachmentId, { onSettled: finished })
    } else {
      acquireMat.mutate({ snapshotAttachmentId }, { onSettled: finished })
    }
  }

  const handleToggleTool = (catalogToolId: string, currentlyAcquired: boolean) => {
    setPendingToolIds((s) => new Set(s).add(catalogToolId))
    const finished = () =>
      setPendingToolIds((s) => {
        const next = new Set(s)
        next.delete(catalogToolId)
        return next
      })
    if (currentlyAcquired) {
      unacquireTool.mutate(catalogToolId, { onSettled: finished })
    } else {
      acquireTool.mutate({ catalogToolId }, { onSettled: finished })
    }
  }

  const handleToggle = (stepId: string, completed: boolean): void => {
    setPendingStepIds((s) => {
      const next = new Set(s)
      next.add(stepId)
      return next
    })
    toggleMutation.mutate(
      { projectId: following.projectId, stepId, completed },
      {
        onSettled: () => {
          setPendingStepIds((s) => {
            const next = new Set(s)
            next.delete(stepId)
            return next
          })
        },
      }
    )
  }

  if (totalActions === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          This project has no Actions to mark complete.
        </CardContent>
      </Card>
    )
  }

  if (completedInTree === totalActions) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-500" />
          <p className="text-base font-medium">You&apos;re done!</p>
          <p className="text-sm text-muted-foreground">All {totalActions} Actions complete.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="py-2">
        <ul className="space-y-0.5">
          {following.snapshotSteps.map((step) => (
            <DoingStepRow
              key={step.id}
              step={step}
              depth={0}
              catalog={following.catalog}
              completedStepIds={completedStepIds}
              onToggleProgress={handleToggle}
              pendingStepIds={pendingStepIds}
              readinessMap={readinessMap}
              acquisitions={{
                materials: materialAcqMap,
                tools: toolAcqMap,
                inventory: following.inventory,
                onToggleMaterial: handleToggleMaterial,
                onToggleTool: handleToggleTool,
                pendingMaterialIds,
                pendingToolIds,
              }}
            />
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

const countActionLeaves = (steps: FollowingDetail['snapshotSteps']): number => {
  let n = 0
  const walk = (list: FollowingDetail['snapshotSteps']): void => {
    for (const s of list) {
      if (s.kind === 'ACTION' && s.children.length === 0) n += 1
      walk(s.children)
    }
  }
  walk(steps)
  return n
}

const countCompletedInTree = (
  steps: FollowingDetail['snapshotSteps'],
  completed: Set<string>
): number => {
  let n = 0
  const walk = (list: FollowingDetail['snapshotSteps']): void => {
    for (const s of list) {
      if (s.kind === 'ACTION' && s.children.length === 0 && completed.has(s.id)) n += 1
      walk(s.children)
    }
  }
  walk(steps)
  return n
}
